import React, { memo, useCallback } from "react";
import PropTypes from "prop-types";
import { getBezierPath, EdgeLabelRenderer } from "reactflow";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useWorkflowBuilder } from "src/contexts/workflowBuilderContext";

const EdgePath = styled.path`
  stroke: ${(props) => (props.$isSelected ? "#1976D2" : "#90A4AE")};
  stroke-width: ${(props) => (props.$isSelected ? 3 : 2)};
  fill: none;
  transition:
    stroke 0.2s ease,
    stroke-width 0.2s ease;
`;

const DeleteButton = styled.button`
  position: absolute;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #f44336;
  border: 2px solid white;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  opacity: 0;
  transition: opacity 0.2s ease;

  &:hover {
    background: #d32f2f;
  }
`;

const EdgeWrapper = styled.g`
  &:hover ${DeleteButton} {
    opacity: 1;
  }
`;

function WorkflowEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
  markerEnd,
}) {
  const { selectEdge, deleteEdge, selectedEdgeId } = useWorkflowBuilder();

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const isSelected = selected || selectedEdgeId === id;

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      selectEdge(id);
    },
    [id, selectEdge]
  );

  const handleDelete = useCallback(
    (e) => {
      e.stopPropagation();
      deleteEdge(id);
    },
    [id, deleteEdge]
  );

  return (
    <EdgeWrapper>
      {/* Invisible wider path for easier click target */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        onClick={handleClick}
        style={{ cursor: "pointer" }}
      />

      <EdgePath
        id={id}
        d={edgePath}
        $isSelected={isSelected}
        onClick={handleClick}
        markerEnd={markerEnd}
        style={{ cursor: "pointer" }}
      />

      <EdgeLabelRenderer>
        <DeleteButton
          style={{
            left: labelX,
            top: labelY,
            opacity: isSelected ? 1 : undefined,
            pointerEvents: "all",
          }}
          onClick={handleDelete}
          title="Delete connection"
        >
          <FontAwesomeIcon icon={["fas", "times"]} />
        </DeleteButton>
      </EdgeLabelRenderer>
    </EdgeWrapper>
  );
}

WorkflowEdge.propTypes = {
  id: PropTypes.string.isRequired,
  sourceX: PropTypes.number.isRequired,
  sourceY: PropTypes.number.isRequired,
  targetX: PropTypes.number.isRequired,
  targetY: PropTypes.number.isRequired,
  sourcePosition: PropTypes.string.isRequired,
  targetPosition: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  markerEnd: PropTypes.string,
};

export default memo(WorkflowEdge);
